import type { FC } from 'react';

import { Placeholder, useCms, useContent, useRendering } from '@eneco/cms';
import { NavLink, Stack, Text } from '@eneco/sparky';

import type { MegaMenuDivisionContent } from './mega-menu-content.types';
import { MEGA_MENU_DIVISION_SLOTS, resolveSlot } from './slots';

/**
 * `MegaMenuDivision` block for the CMS-agnostic lane, serving **both** providers. One column of the
 * mega menu: either a direct link, or a heading over a set of cards.
 *
 * Known simplification: the legacy component is an interactive disclosure — a Radix trigger opening
 * a desktop dropdown, a full-screen dialog with a back button on mobile — built from
 * `@eneco/custom-components/MegaMenu`, which is outside this lib's module boundary (`nx.json`). Here
 * the heading and its cards render inline, always expanded. The content is the same; the interaction
 * is not reproduced.
 */
const MegaMenuDivision: FC = () => {
  const { adapter } = useCms();
  const content = useContent<MegaMenuDivisionContent>();
  const rendering = useRendering();

  const link = adapter.toLink(content.link);
  const title = content.title ?? content.siteWideContent?.title;
  const cardsSlot = resolveSlot(rendering, MEGA_MENU_DIVISION_SLOTS.cards);

  // A division that is only a link renders as one, exactly as the legacy component does.
  if (link?.href && !cardsSlot) {
    return (
      <NavLink href={link.href} target={link.target}>
        <Text size="BodyL">{link.text ?? title}</Text>
      </NavLink>
    );
  }

  if (!title && !cardsSlot) return null;

  return (
    <Stack gap="4">
      {title && (
        <Text size="BodyL" weight="bold">
          {title}
        </Text>
      )}
      {cardsSlot && <Placeholder name={cardsSlot} />}
    </Stack>
  );
};

export default MegaMenuDivision;
